import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { cloneDeep, find, get, map } from "lodash";
import {
  Button as ButtonUpdate,
  FormControl,
  FormHelperText,
  InputLabel,
  TextField,
  TextareaAutosize,
  Select,
  MenuItem,
} from '@mui/material';
import { Card, Col, Row, Divider, message, Tag } from "antd";
import Loading from "components/Loading";
import { useStandards } from "store/story/selectors";
import { handleCreateStanders, handleUpdateStanders, handleGenerateStateStandards, handleApproveStandard } from "store/story/thunks";
import { STANDARD_GRADE_LEVEL } from "utils/config/reference";
import { normalizeGradeLevel, isIllinoisOrCommonCoreState, getStateCodeFromName, getStateNameFromCode, getStateNameDisplayForCode, statesMatch } from "utils/lib/standardHelper";
import styled from "styled-components";
import { AutoAwesomeSharp } from "@mui/icons-material";

const TextAreaAuto = styled(TextareaAutosize)`
  height: 200px;
  max-height: 210px;
  width: 100%;
  border-radius: 2px;
  padding: 5px;
  overflow: hidden;
  border-color: rgb(0 0 0 / 32%);
  ::focus-visible {
    outline: #03989e 1px !important;
    border: 5px solid red !important;
  }
  ::focus {
    border-color: #03989e !important;
  }
`;

const StandardForm = ({ standers, onChangeStanders, defaultState, defaultGrade }) => {
  const isEditMode = !!standers;
  const isDraft = isEditMode && (standers?.status === false || standers?.status === 0);
  
  const getDefaultStateValue = () => {
    if (defaultState) {
      if (defaultState.code) return defaultState.code;
      const defaultStateName = getStateNameFromCode(defaultState.code) || defaultState.state;
      return getStateCodeFromName(defaultStateName) || defaultStateName;
    }
    return getStateCodeFromName("Common Core") || "COMMONCORE";
  };

  const [standerData, setStanderData] = useState({
    abbr: "",
    abbr_spanish: "",
    description: "",
    description_english: "",
    grade_level: defaultGrade || "",
    state: getDefaultStateValue(),
  });
  const [error, setError] = useState(null);
  const [abbrError, setAbbrError] = useState(null);
  const allStanderds = useStandards();
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(false);
  const [generating, setGenerating] = useState(false);

  const normalizeGradeForDropdown = (gradeLevel, state) => {
    if (!gradeLevel) return gradeLevel;
    const normalized = normalizeGradeLevel(gradeLevel);
    
    if (isIllinoisOrCommonCoreState(state)) {
      if (normalized === "9" || normalized === "10") {
        return "9-10";
      }
      if (normalized === "11" || normalized === "12") {
        return "11-12";
      }
    }
    
    return normalized;
  };

  useEffect(() => {
    if (standers) {
      const standerStateName = getStateNameFromCode(standers.state) || standers.state;
      const stateDropdownValue = standerStateName && standerStateName.toUpperCase().trim() === "COMMON CORE"
        ? "COMMONCORE"
        : (getStateCodeFromName(standerStateName) || standers.state);
      const normalizedGrade = normalizeGradeForDropdown(standers.grade_level, stateDropdownValue);
      
      setStanderData({
        ...standers,
        description_english: standers.description_english || "",
        abbr_spanish: standers.abbr_spanish || "",
        state: stateDropdownValue,
        grade_level: normalizedGrade,
      });
    } else {
      // Reset form when adding new standard
      setStanderData({
        abbr: "",
        abbr_spanish: "",
        description: "",
        description_english: "",
        grade_level: defaultGrade || "",
        state: getDefaultStateValue(),
      });
    }
    setError(null);
    setAbbrError(null);
    setLoader(false);
    setGenerating(false);
  }, [standers, defaultState, defaultGrade]);

  const onChangeData = (key, value) => {
    const res = cloneDeep(standerData);
    if (key === "abbr") {
      const trimmedValue = value.slice(0, 100).toUpperCase();
      const hasAllReady = find(
        allStanderds,
        (std) => {
          const existingStateName = getStateNameFromCode(std.state) || std.state;
          return (isEditMode ? std.id != standerData.id : true) &&
            trimmedValue.toLowerCase() == std.abbr.toLowerCase() && 
            statesMatch(existingStateName, res.state) && 
            std.grade_level == res.grade_level;
        }
      );
      if (hasAllReady) {
        setAbbrError("Abbreviation already defined please use different");
      } else {
        setAbbrError(null);
      }
      res[key] = trimmedValue;
    } else if (key === "state") {
      res[key] = value;
      const currentGrade = res.grade_level;
      
      if (currentGrade) {
        const normalized = normalizeGradeLevel(currentGrade);
        if (isIllinoisOrCommonCoreState(value)) {
          if (normalized === "9" || normalized === "10") {
            res.grade_level = "9-10";
          } else if (normalized === "11" || normalized === "12") {
            res.grade_level = "11-12";
          }
        }
      }
    } else if (key === "grade_level") {
      res[key] = value;
    } else {
      res[key] = value;
    }
    setStanderData(res);
    setError(null);
  };

  const handleGenerateEnglishDescription = useCallback(async () => {
    const currentData = standerData;
    if (!currentData.description) {
      setError("Please fill in description first");
      return;
    }
    setGenerating(true);
    setError(null);
    try {
      const generatedStates = await dispatch(handleGenerateStateStandards({
        abbr: currentData.abbr || "",
        description: currentData.description,
        grade_level: currentData.grade_level || ""
      }));
      if (generatedStates && Array.isArray(generatedStates)) {
        const defaultGenerated = generatedStates.find((s) => {
          const sStateName = getStateNameFromCode(s.code) || s.state;
          return sStateName === "Common Core" || s.state === "Common Core State Standards (en español)";
        });
        if (defaultGenerated && defaultGenerated.description_english) {
          setStanderData((prevData) => ({
            ...prevData,
            description_english: defaultGenerated.description_english,
          }));
          message.success("English description generated successfully!");
        } else {
          setError("Could not generate English description. Please try again.");
        }
      } else {
        setError("Invalid response from server. Please try again.");
      }
    } catch (error) {
      setError("Failed to generate English description. Please try again.");
      console.error("Generate error:", error);
    } finally {
      setGenerating(false);
    }
  }, [standerData.description, dispatch]);

  const onSubmit = async ({ approveAfterUpdate = false } = {}) => {
    if (!standerData.abbr) {
      setError("Abbreviation is required");
      return;
    } else if (!standerData.description) {
      setError("Description is required");
      return;
    } else if (!standerData.grade_level) {
      setError("Grade level is required");
      return;
    }
    const hasAllReady = find(
      allStanderds,
      (std) => {
        const existingStateName = getStateNameFromCode(std.state) || std.state;
        return (isEditMode ? std.id != standerData.id : true) &&
          standerData.abbr.toLowerCase() == std.abbr.toLowerCase() && 
          statesMatch(existingStateName, standerData.state) && 
          standerData.grade_level == std.grade_level;
      }
    );
    if (hasAllReady) {
      setError("Abbreviation already defined please use different");
      return;
    }

    setError(null);
    setLoader(true);
    
    const stateUpper = (standerData.state || "").toUpperCase().trim();
    // Convert state to name (except Common Core -> COMMONCORE), uppercase and trim
    let stateForAPI;
    if (stateUpper === "COMMONCORE" || stateUpper === "COMMON CORE") {
      stateForAPI = "COMMONCORE";
    } else {
      stateForAPI = getStateNameFromCode(standerData.state) || standerData.state;
    }
    
    // Uppercase and trim (except Common Core which is already COMMONCORE)
    if (stateForAPI !== 'COMMONCORE') {
      stateForAPI = stateForAPI.toUpperCase().trim();
    }
    
    const submitData = {
      ...standerData,
      state: stateForAPI
    };
    if (isEditMode) {
      await dispatch(handleUpdateStanders(submitData));
      if (approveAfterUpdate && standerData?.id) {
        await dispatch(handleApproveStandard(standerData.id, true));
      }
    } else {
      await dispatch(handleCreateStanders([submitData]));
    }
    setLoader(false);
    onChangeStanders();
  };

  return (
    <div style={{ paddingTop: "8px", paddingBottom: "8px", paddingLeft: "8px", paddingRight: "8px", display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ flexShrink: 0 }}>
        <Card 
          title={
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>
                {getStateNameFromCode(get(standerData, "state")) || get(standerData, "state", "Common Core")}
              </span>
            </div>
          }
          style={{ 
            marginBottom: 12,
            border: "2px solid #1890ff",
            backgroundColor: "#f0f7ff"
          }}
          size="small"
          headStyle={{ 
            backgroundColor: "#e6f4ff",
            borderBottom: "2px solid #1890ff",
            paddingTop: "12px",
            paddingBottom: "12px",
            paddingLeft: "16px",
            paddingRight: "16px"
          }}
          bodyStyle={{ 
            backgroundColor: "#fafafa",
            paddingTop: "16px",
            paddingBottom: "16px",
            paddingLeft: "16px",
            paddingRight: "16px"
          }}
        >
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={12}>
              <TextField
                id="title"
                name="title"
                label="Abbreviation"
                slotProps={{ inputLabel: { shrink: true } }}
                type="text"
                variant="outlined"
                onChange={(e) => {
                  onChangeData("abbr", e.target.value);
                }}
                value={get(standerData, "abbr", "")}
                fullWidth
                inputProps={{ maxLength: 100 }}
              />
              {abbrError && (
                <FormHelperText style={{ color: "red", marginTop: 4 }}>
                  {abbrError}
                </FormHelperText>
              )}
            </Col>
            <Col xs={24} sm={12}>
              <TextField
                id="abbr_spanish"
                name="abbr_spanish"
                label="Spanish Abbreviation"
                slotProps={{ inputLabel: { shrink: true } }}
                type="text"
                variant="outlined"
                onChange={(e) => {
                  onChangeData("abbr_spanish", e.target.value);
                }}
                value={get(standerData, "abbr_spanish", "")}
                fullWidth
                inputProps={{ maxLength: 100 }}
              />
            </Col>
            <Col xs={24} sm={12}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel
                  id="state_label"
                  style={{
                    backgroundColor: "#fff",
                    paddingLeft: 2,
                    paddingRight: 2,
                  }}
                >
                  State
                </InputLabel>
                <Select
                  id="state"
                  name="state"
                  label="State"
                  value={get(standerData, "state", "Common Core")}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    onChangeData("state", selectedValue);
                  }}
                  slotProps={{ inputLabel: { shrink: true } }}
                >
                  {STANDARD_GRADE_LEVEL.map((state) => {
                    const stateName = getStateNameFromCode(state.code) || state.state;
                    const displayValue = getStateCodeFromName(stateName) || state.code;
                    const label = getStateNameDisplayForCode(stateName) || stateName;
                    return (
                      <MenuItem key={state.code} value={displayValue}>
                        {label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Col>
            <Col xs={24} sm={12}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel
                  id="grade_level_label"
                  style={{
                    backgroundColor: "#fff",
                    paddingLeft: 2,
                    paddingRight: 2,
                  }}
                >
                  Grade Level
                </InputLabel>
                <Select
                  id="grade_level"
                  name="grade_level"
                  label="Grade Level"
                  value={get(standerData, "grade_level", "")}
                  onChange={(e) => {
                    onChangeData("grade_level", e.target.value);
                  }}
                  slotProps={{ inputLabel: { shrink: true } }}
                >
                  {(() => {
                    const currentState = get(standerData, "state", "Common Core");
                    const isCommonCoreOrIllinois = isIllinoisOrCommonCoreState(currentState);
                    
                    if (isCommonCoreOrIllinois) {
                      return [
                        <MenuItem key="6" value="6">6</MenuItem>,
                        <MenuItem key="7" value="7">7</MenuItem>,
                        <MenuItem key="8" value="8">8</MenuItem>,
                        <MenuItem key="9-10" value="9-10">9-10</MenuItem>,
                        <MenuItem key="11-12" value="11-12">11-12</MenuItem>
                      ];
                    } else {
                      return [
                        <MenuItem key="6" value="6">6</MenuItem>,
                        <MenuItem key="7" value="7">7</MenuItem>,
                        <MenuItem key="8" value="8">8</MenuItem>,
                        <MenuItem key="9" value="9">9</MenuItem>,
                        <MenuItem key="10" value="10">10</MenuItem>,
                        <MenuItem key="11" value="11">11</MenuItem>,
                        <MenuItem key="12" value="12">12</MenuItem>
                      ];
                    }
                  })()}
                </Select>
              </FormControl>
            </Col>
            <Col xs={24}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel
                  id="textTip"
                  style={{
                    backgroundColor: "#fff",
                    paddingLeft: 2,
                    paddingRight: 2,
                  }}
                >
                  Description
                </InputLabel>
                <TextAreaAuto
                  id="description"
                  name="description"
                  label="Description"
                  type="text"
                  variant="outlined"
                  onChange={(e) => {
                    onChangeData("description", e.target.value);
                  }}
                  value={get(standerData, "description", "")}
                  style={{ height: 56, borderRadius: 4, overflow: "scroll", fontSize: "14px", paddingTop: "8px", paddingBottom: "8px", paddingLeft: "8px", paddingRight: "8px" }}
                  maxRows={5}
                  minRows={1}
                />
              </FormControl>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px', marginBottom: '8px' }}>
                <ButtonUpdate
                  onClick={handleGenerateEnglishDescription}
                  disabled={!standerData.description || generating}
                  variant="outlined"
                  size="small"
                  startIcon={generating ? <Loading size={14} /> : <AutoAwesomeSharp />}
                  style={{
                    fontSize: '12px',
                    textTransform: 'none'
                  }}
                >
                  {generating ? 'Generating...' : 'Generate English'}
                </ButtonUpdate>
              </div>
            </Col>
            <Col xs={24}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel
                  id="textTipEnglish"
                  style={{
                    backgroundColor: "#fff",
                    paddingLeft: 2,
                    paddingRight: 2,
                  }}
                >
                  Description (English)
                </InputLabel>
                <TextAreaAuto
                  id="description_english"
                  name="description_english"
                  label="Description (English)"
                  type="text"
                  variant="outlined"
                  onChange={(e) => {
                    onChangeData("description_english", e.target.value);
                  }}
                  value={get(standerData, "description_english", "")}
                  style={{ height: 56, borderRadius: 4, overflow: "scroll", fontSize: "14px", paddingTop: "8px", paddingBottom: "8px", paddingLeft: "8px", paddingRight: "8px" }}
                  maxRows={5}
                  minRows={1}
                />
              </FormControl>
            </Col>
          </Row>
        </Card>
      </div>
      <div style={{ flexShrink: 0, marginTop: 12 }}>
        {error && (
          <FormHelperText error style={{ marginBottom: 8 }}>
            {error}
          </FormHelperText>
        )}
        <Row
          gutter={[24, 24]}
          style={{ marginRight: 0, marginLeft: 0 }}
        >
          {isDraft ? (
            <>
              <ButtonUpdate
                onClick={() => onSubmit({ approveAfterUpdate: true })}
                color="primary"
                variant="contained"
                disabled={!standerData || loader}
                style={{ minWidth: 200 }}
              >
                {loader ? <Loading white size={14} /> : "Update and Approve"}
              </ButtonUpdate>
            </>
          ) : (
            <ButtonUpdate
              onClick={() => onSubmit()}
              color="primary"
              variant="contained"
              disabled={!standerData || loader}
              style={{ minWidth: 200 }}
            >
              {loader ? <Loading white size={14} /> : isEditMode ? "Update" : "Create"}
            </ButtonUpdate>
          )}
        </Row>
      </div>
    </div>
  );
};

export default StandardForm;
